import React, { useCallback, useMemo } from "react";
import classNames from "classnames";
import { useFormikContext } from "formik";
import { TransferRuleDirection } from "@common/types/transactionRule";
import { Button } from "@design-system/Button/Button";
import { PlusIcon, ArrowDownIcon, ArrowUpIcon } from "@icons";
import styles from "../TransferRuleForm.module.scss";
import { FormRuleConditionType } from "../../TransactionRuleForm/constants";
import { getDefaultFormRow } from "../../TransactionRuleForm/modelToForm.helpers";
import { TransferRuleFormSchema } from "../constants";
import { TransferRuleConditionForm } from "./TransferRuleConditionForm";
import { Tooltip } from "@design-system/Tooltip/Tooltip";

export const TransferRuleConditionsForm = ({
    transferDirection,
}: {
    transferDirection: TransferRuleDirection;
}) => {
    const { values, setFieldValue } =
        useFormikContext<TransferRuleFormSchema>();

    const conditions = values.conditions[transferDirection];

    const addCondition = useCallback(() => {
        setFieldValue("conditions", {
            ...values.conditions,
            [transferDirection]: [
                ...conditions,
                {
                    id: Date.now().toString(),
                    ...getDefaultFormRow(),
                    transferDirection,
                },
            ],
        });
    }, [conditions, setFieldValue, transferDirection, values.conditions]);

    const removeCondition = useCallback(
        (id: string) => {
            setFieldValue("conditions", {
                ...values.conditions,
                [transferDirection]: conditions.filter(
                    (condition) => condition.id !== id,
                ),
            });
        },
        [conditions, setFieldValue, transferDirection, values.conditions],
    );

    const selectedConditionTypes = useMemo(
        () =>
            conditions
                .map((condition) => condition.conditionType)
                .filter(
                    (v) =>
                        v !== undefined &&
                        v !== FormRuleConditionType.AMOUNT &&
                        v !== FormRuleConditionType.BANK_DESCRIPTION,
                ),
        [conditions],
    );

    const canAddCondition = useMemo(
        () => conditions.length < Object.values(FormRuleConditionType).length,
        [conditions.length],
    );

    return (
        <section className={styles.sectionConditions}>
            <header className={styles.header}>
                <aside
                    className={classNames(
                        styles.sectionIcon,
                        styles.sectionIconConditions,
                    )}
                >
                    {transferDirection === TransferRuleDirection.FROM ? (
                        <ArrowDownIcon className="icon-color-gray-700" />
                    ) : (
                        <ArrowUpIcon className="icon-color-gray-700" />
                    )}
                </aside>
                <span className={styles.sectionTitle}>
                    {transferDirection === TransferRuleDirection.FROM
                        ? "From"
                        : "To"}
                </span>
            </header>

            <main className={styles.content}>
                <div className={styles.sectionRows}>
                    {conditions?.map((condition, index) => (
                        <div
                            className={styles.sectionRow}
                            key={`${condition.id}-${index}-${condition.conditionType}`}
                        >
                            <TransferRuleConditionForm
                                condition={condition}
                                index={index}
                                onRemove={removeCondition}
                                selectedConditionTypes={selectedConditionTypes}
                                transferDirection={transferDirection}
                            />
                        </div>
                    ))}

                    <div className={styles.sectionRow}>
                        <Tooltip
                            content={
                                canAddCondition
                                    ? undefined
                                    : "You can add only one condition per type"
                            }
                        >
                            <Button
                                variant="primary"
                                size="md"
                                leftIcon={<PlusIcon />}
                                onClick={addCondition}
                                type="button"
                                disabled={!canAddCondition}
                            >
                                Add
                            </Button>
                        </Tooltip>
                    </div>
                </div>
            </main>
        </section>
    );
};
